import { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import '../../../utils/pdfWorkerConfig';

export const THUMBNAIL_WIDTH = 120;

const usePageRenderer = (documentUrl) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const thumbnailCacheRef = useRef(new Map());
  const previewCacheRef = useRef(new Map());

  useEffect(() => {
    let mounted = true;

    const loadDocument = async () => {
      if (!documentUrl) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const loadingTask = pdfjsLib.getDocument({
          url: documentUrl,
          disableWorker: false,
          // CVE-2024-4367: see utils/pdfSafety.js.
          isEvalSupported: false,
        });

        const pdf = await loadingTask.promise;
        
        if (mounted) {
          setPdfDoc(pdf);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err.message || 'Failed to load PDF');
          setLoading(false);
        }
      }
    };

    loadDocument();

    return () => {
      mounted = false;
      thumbnailCacheRef.current.clear();
      previewCacheRef.current.clear();
    };
  }, [documentUrl]);

  // `width` is the bitmap's width in px. Document Studio never passes it and
  // gets the 120px thumbnails it always had; the split review rail passes one of
  // a few fixed steps (utils/splitRailPrefs.js), so each size is cached apart.
  const renderThumbnail = useCallback(async (pageNumber, rotation = 0, width = THUMBNAIL_WIDTH) => {
    if (!pdfDoc) return null;

    const cacheKey = `${pageNumber}-${rotation}-${width}`;
    if (thumbnailCacheRef.current.has(cacheKey)) {
      return thumbnailCacheRef.current.get(cacheKey);
    }

    try {
      const page = await pdfDoc.getPage(pageNumber + 1);
      const viewport = page.getViewport({ scale: 1, rotation });
      
      const scale = width / viewport.width;
      const scaledViewport = page.getViewport({ scale, rotation });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;

      await page.render({
        canvasContext: context,
        viewport: scaledViewport,
      }).promise;

      const dataUrl = canvas.toDataURL();
      thumbnailCacheRef.current.set(cacheKey, dataUrl);
      return dataUrl;
    } catch (err) {
      console.error('[usePageRenderer] Error rendering thumbnail:', err);
      return null;
    }
  }, [pdfDoc]);

  const renderPreview = useCallback(async (pageNumber, rotation = 0) => {
    if (!pdfDoc) return null;

    const cacheKey = `preview-${pageNumber}-${rotation}`;
    if (previewCacheRef.current.has(cacheKey)) {
      return previewCacheRef.current.get(cacheKey);
    }

    try {
      const page = await pdfDoc.getPage(pageNumber + 1);
      const viewport = page.getViewport({ scale: 1, rotation });
      
      const scale = 600 / viewport.width;
      const scaledViewport = page.getViewport({ scale, rotation });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;

      await page.render({
        canvasContext: context,
        viewport: scaledViewport,
      }).promise;

      const dataUrl = canvas.toDataURL();
      previewCacheRef.current.set(cacheKey, dataUrl);
      return dataUrl;
    } catch (err) {
      console.error('[usePageRenderer] Error rendering preview:', err);
      return null;
    }
  }, [pdfDoc]);

  const clearCache = useCallback(() => {
    thumbnailCacheRef.current.clear();
    previewCacheRef.current.clear();
  }, []);

  return {
    pdfDoc,
    loading,
    error,
    pageCount: pdfDoc?.numPages || 0,
    renderThumbnail,
    renderPreview,
    clearCache,
  };
};

export default usePageRenderer;
